import { patch } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

export const WORKER_OUTCOME_RESULT = {
  gotOffer: "got-offer",
  gotOfferKnowNextSteps: "got-offer-know-next-steps",
  gotOfferFollowUpNextSteps: "got-offer-follow-up-next-steps",
  gotOfferNoNextSteps: "got-offer-no-next-steps",
  notHeardBack: "not-heard-back",
  notHeardBackFewDays: "not-heard-back-few-days",
  notHeardBackWithinWeek: "not-heard-back-within-week",
  notHeardBackNotSpecified: "not-heard-back-not-specified",
  scheduleMisalignment: "schedule-misalignment",
  payMisalignment: "pay-misalignment",
  notEnoughExperience: "not-enough-experience",
  notSure: "not-sure",
  notOfferTheRoleOther: "not-offer-the-role-other",
  interviewNotAvailable: "interview-not-available",
  interviewerUnavailableWorkerLeft: "interviewer-unavailable-worker-left",
  noLongerHiring: "no-longer-hiring",
  fillOutApplicationFirst: "fill-out-application-first",
  notInterviewOther: "not-interview-other",
} as const;
export type WorkerOutcomeResult =
  (typeof WORKER_OUTCOME_RESULT)[keyof typeof WORKER_OUTCOME_RESULT];

const updateInterviewBodySchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.interview),
    id: z.string(),
    attributes: z.object({
      status: z.string().optional(),
      workerOutcome: z
        .object({
          dismissedAt: z.date().optional(),
          result: z.nativeEnum(WORKER_OUTCOME_RESULT).optional(),
          message: z.string().optional(),
        })
        .optional(),
    }),
  }),
});

export type UpdateInterviewBody = z.infer<typeof updateInterviewBodySchema>;

interface UpdateInterviewParams {
  interviewId: string;
  attributes: UpdateInterviewBody["data"]["attributes"];
}

export function useUpdateInterview(
  options: UseMutationOptions<void, AxiosError, UpdateInterviewParams> = {}
) {
  return useMutation({
    mutationFn: async ({ interviewId, attributes }) => {
      await patch({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/interviews/${interviewId}`,
        data: {
          data: {
            type: API_TYPES.interview,
            id: interviewId,
            attributes,
          },
        },
        requestSchema: updateInterviewBodySchema,
        responseSchema: z.unknown(),
      });

      logEvent(APP_V2_APP_EVENTS.UPDATE_INTERVIEW_SUCCESS, {
        interviewId,
        attributes,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_INTERVIEW_FAILURE,
    },
    ...options,
  });
}
